import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, Calculator } from "lucide-react";
import { useLanguage, waLink } from "../i18n";
import LangToggle from "./LangToggle";
import { EstimatorButton, useEstimator } from "./QuoteEstimator";
import { AvailabilityBar } from "./Availability";

const scrollTo = (hash) => {
  if (window.__lenis) window.__lenis.scrollTo(hash, { offset: -104 });
  else document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
};

const Nav = () => {
  const { t } = useLanguage();
  const { open: openEstimator } = useEstimator();
  const [open, setOpen] = useState(false);
  const links = [
    { label: t.nav.home, hash: "#inicio", id: "home" },
    { label: t.nav.services, hash: "#servicios", id: "services" },
    { label: t.nav.gallery, hash: "#galeria", id: "gallery" },
    { label: t.nav.owners, hash: "#propietarios", id: "owners" },
    { label: t.nav.zones, hash: "#zonas", id: "zones" },
    { label: t.nav.contact, hash: "#contacto", id: "contact" },
  ];

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 2.1, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-[#0D1B2A]/10 bg-[#FAF6F0]/85 backdrop-blur-md"
      data-testid="main-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <button
          data-testid="header-logo"
          onClick={() => scrollTo("#inicio")}
          className="group flex items-center"
        >
          <img
            src="/images/logo.png"
            alt="CasaFix Marina · Manitas y mantenimiento"
            className="h-14 w-auto transition-transform duration-300 group-hover:scale-105"
          />
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.hash)}
              className="text-sm font-medium text-[#0D1B2A]/70 transition-colors duration-300 hover:text-[#C85A32]"
            >
              {l.label}
            </button>
          ))}
          <EstimatorButton
            testId="nav-estimator-button"
            iconSize={15}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#1B6B93]/30 px-3.5 py-1.5 text-sm font-semibold text-[#1B6B93] transition-colors duration-300 hover:border-[#1B6B93] hover:bg-[#1B6B93] hover:text-white"
          />
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle />
          <a
            data-testid="header-whatsapp-link"
            href={waLink(t.waGeneric)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/30 transition-transform duration-300 hover:scale-105 sm:inline-flex"
          >
            <MessageCircle size={16} strokeWidth={2.4} />
            {t.nav.cta}
          </a>
          <button
            data-testid="nav-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#0D1B2A]/15 text-[#0D1B2A] lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-[#0D1B2A]/10 bg-[#FAF6F0] lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => {
                    setOpen(false);
                    scrollTo(l.hash);
                  }}
                  className="border-b border-[#0D1B2A]/8 py-3 text-left font-serif text-xl text-[#0D1B2A] last:border-0"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-estimator-button"
                onClick={() => {
                  setOpen(false);
                  openEstimator();
                }}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#1B6B93] py-3 font-semibold text-white"
              >
                <Calculator size={16} strokeWidth={2.4} />
                {t.estimate.open}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
      <AvailabilityBar />
    </motion.header>
  );
};

export default Nav;
